import React, { useEffect, useState } from "react";
import {
    Container,
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    Grid,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import api from "../../services/api";
import { userHasActiveEntitlement } from "../../utils/entitlements";

export default function ShoppingListsIndex() {
    const { t, i18n } = useTranslation();
    const { country } = useParams();
    const navigate = useNavigate();

    const [lists, setLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isPremium, setIsPremium] = useState(false);
    const [updatingPrimaryId, setUpdatingPrimaryId] = useState(null);
    const [premiumModalOpen, setPremiumModalOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const lang = i18n.language === "el" ? "gr" : i18n.language;

    useEffect(() => {
        let mounted = true;

        const loadLists = async () => {
            setLoading(true);
            setErrorMsg("");

            try {
                const [meResult, entitlementsResult] = await Promise.allSettled([
                    api.users.me(),
                    api.users.entitlements(),
                ]);
                if (meResult.status !== "fulfilled") {
                    throw meResult.reason;
                }
                const meRes = meResult.value;
                const entitlements = entitlementsResult.status === "fulfilled"
                    ? entitlementsResult.value?.data?.entitlements || null
                    : null;
                const authenticated = Boolean(
                    meRes?.data?.audience?.is_authenticated ||
                    meRes?.data?.is_authenticated
                );

                if (!mounted) return;
                setIsAuthenticated(authenticated);
                const meUser = meRes?.data?.user ? { ...meRes.data.user, entitlements } : null;
                setIsPremium(userHasActiveEntitlement(meUser));

                const listRes = authenticated
                    ? await api.shoppingList.listAll(lang)
                    : await api.guestShoppingList.listAll(lang);

                if (!mounted) return;

                const arr = listRes?.data?.lists || [];
                setLists(Array.isArray(arr) ? arr : []);
            } catch (err) {
                if (!mounted) return;

                const msg =
                    err?.response?.data?.message ||
                    err?.response?.data?.error ||
                    err?.message ||
                    "Failed to load lists";
                setErrorMsg(msg);
                setLists([]);
            } finally {
                if (!mounted) return;
                setLoading(false);
            }
        };

        loadLists();

        return () => {
            mounted = false;
        };
    }, [lang, country]); // ✅ ξανατρέχει και όταν αλλάζει χώρα

    const handleOpenList = (id) => {
        const query = isAuthenticated ? `list_id=${id}` : `guest=1&list_id=${id}`;
        navigate(`/${country}/client/shopping/list?${query}`);
    };

    const handleOpenAggregate = () => {
        navigate(`/${country}/client/shopping/list?mode=aggregate`);
    };

    const handleTogglePrimary = async (listId, nextValue) => {
        setUpdatingPrimaryId(listId);
        setErrorMsg("");
        try {
            await api.shoppingList.setPrimary(listId, nextValue);
            setLists((prev) =>
                (prev || []).map((list) => ({
                    ...list,
                    is_primary: nextValue ? list.id === listId : (list.id === listId ? false : list.is_primary),
                }))
            );
            setSnackbarOpen(true);
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                "Failed to update primary shopping list";
            setErrorMsg(msg);
        } finally {
            setUpdatingPrimaryId(null);
        }
    };

    const handlePrimaryIntent = (listId, nextValue) => {
        if (!isAuthenticated || !isPremium) {
            setPremiumModalOpen(true);
            return;
        }
        handleTogglePrimary(listId, nextValue);
    };

    return (
        <>
        <Container sx={{ mt: 4 }}>
            <Box
                sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <ShoppingBasketIcon color="primary" sx={{ fontSize: 36 }} />
                <Typography variant="h5">
                    {t("my_shopping_lists_title") || "Οι λίστες αγορών μου"}
                </Typography>
            </Box>

            {/* ✅ Error message */}
            {errorMsg ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMsg}
                </Alert>
            ) : null}

            {/* ✅ Loading */}
            {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography>Loading...</Typography>
                </Box>
            ) : lists.length === 0 ? (
                <Typography>
                    {t("no_saved_lists") || "Δεν έχεις ακόμα αποθηκευμένες λίστες αγορών."}
                </Typography>
            ) : (
                <>
                    {/* ✅ Το κουμπί μπαίνει ΕΞΩ από το map (1 φορά, όχι 10 φορές) */}
                    {isAuthenticated ? (
                        <Button
                            variant="outlined"
                            sx={{ mb: 2 }}
                            onClick={handleOpenAggregate}
                        >
                            {t("all_products_combined") || "Όλα τα προϊόντα (συγχωνευμένα)"}
                        </Button>
                    ) : null}

                    <Grid container spacing={2}>
                        {lists.map((l) => (
                            <Grid item xs={12} md={6} lg={4} key={l.id}>
                                <Paper sx={{ p: 2, borderRadius: 3 }}>
                                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
                                        <Typography variant="h6">
                                            {l.name || t("unnamed_list") || "Λίστα χωρίς όνομα"}
                                        </Typography>
                                        {isAuthenticated ? (
                                            <Tooltip title={t("set_primary_shopping_list") || "Ορισμός ως κύρια"}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color={l.is_primary ? "warning" : "default"}
                                                        disabled={updatingPrimaryId === l.id}
                                                        onClick={() => handlePrimaryIntent(l.id, !Boolean(l.is_primary))}
                                                    >
                                                        {l.is_primary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        ) : null}
                                    </Box>

                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        {t("items") || "Προϊόντα"}: <b>{l.items_count ?? 0}</b>
                                    </Typography>

                                    <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
                                        {l.source ? (
                                            <Chip
                                                size="small"
                                                label={l.source === "open" ? "Open" : l.source}
                                                variant="outlined"
                                            />
                                        ) : null}

                                        {l.status ? (
                                            <Chip
                                                size="small"
                                                label={l.status}
                                                color={l.status === "open" ? "success" : "default"}
                                                variant="outlined"
                                            />
                                        ) : null}
                                        {l.is_primary ? (
                                            <Chip
                                                size="small"
                                                label={t("primary_shopping_list") || "Κύρια"}
                                                color="warning"
                                                variant="filled"
                                            />
                                        ) : null}
                                    </Box>

                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleOpenList(l.id)}
                                    >
                                        {t("open_list") || "Άνοιγμα λίστας"}
                                    </Button>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
        </Container>
        <Dialog open={premiumModalOpen} onClose={() => setPremiumModalOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>{t("premium_required_title") || "Απαιτείται Premium"}</DialogTitle>
            <DialogContent>
                <Typography variant="body2">
                    {t("premium_required_desc") || "Αυτή η λειτουργία είναι διαθέσιμη μόνο για χρήστες Premium."}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setPremiumModalOpen(false)}>
                    {t("cancel") || "Ακύρωση"}
                </Button>
                <Button variant="contained" onClick={() => navigate(`/${country}/plans?intent=premium`)}>
                    {t("upgrade_to_premium") || "Αναβάθμιση σε Premium"}
                </Button>
            </DialogActions>
        </Dialog>
        <Snackbar
            open={snackbarOpen}
            autoHideDuration={2500}
            onClose={() => setSnackbarOpen(false)}
            message={t("primary_list_set_success") || "Ορίστηκε ως κύρια λίστα"}
        />
        </>
    );
}
